import { type Page, success, type UserInfo } from "@iflyrpa/share";
import type { Response } from "playwright-core";
import type { SteelAction } from "./index";

const CREATOR_PUBLISH_URL = "https://creator.xiaohongshu.com/publish/publish?source=official";

/** 小红书页面全局状态结构 */
interface InitialState {
    user?: {
        userInfo?: {
            _rawValue?: {
                id: number;
                nickname: string;
                images?: string;
                userId?: string;
            };
        };
    };
}

interface CookieLike {
    domain?: string;
}

interface CreatorUserInfoPayload {
    data?: {
        userId?: string;
        userName?: string;
        userAvatar?: string;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

interface StageError extends Error {
    stage: string;
}

const createStageError = (message: string, stage: string): StageError => Object.assign(new Error(message), { stage });

export const isCreatorPublishUrl = (url: string): boolean => {
    try {
        const parsedUrl = new URL(url);
        return parsedUrl.origin === "https://creator.xiaohongshu.com" && parsedUrl.pathname === "/publish/publish";
    } catch {
        return false;
    }
};

export const isExploreUrl = (url: string): boolean => {
    try {
        const parsedUrl = new URL(url);
        return parsedUrl.origin === "https://www.xiaohongshu.com" && parsedUrl.pathname === "/explore";
    } catch {
        return false;
    }
};

export const filterCreatorCookies = <T extends CookieLike>(cookies: T[]): T[] =>
    cookies.filter((cookie) => {
        const normalizedDomain = (cookie.domain ?? "").replace(/^\./, "");
        return normalizedDomain === "creator.xiaohongshu.com" || normalizedDomain === "xiaohongshu.com";
    });

export const mergeUserInfo = (current: UserInfo, next: UserInfo): UserInfo => ({
    name: next.name || current.name,
    avatar: next.avatar || current.avatar,
    uniqueId: next.uniqueId || current.uniqueId,
});

export const extractCreatorUserInfo = (payload: CreatorUserInfoPayload): UserInfo => ({
    name: payload.data?.userName ?? "",
    avatar: payload.data?.userAvatar ?? "",
    uniqueId: payload.data?.userId ?? "",
});

const readUserInfo = async (page: Page): Promise<UserInfo> =>
    page.evaluate(() => {
        const raw = (
            window as unknown as {
                __INITIAL_STATE__?: InitialState;
            }
        ).__INITIAL_STATE__?.user?.userInfo?._rawValue;

        return {
            name: raw?.nickname ?? "",
            avatar: raw?.images ?? "",
            uniqueId: raw?.userId ?? "",
        };
    });

/**
 * 小红书登录 RPA
 *
 * 流程：
 * 1. 打开小红书登录页，生成远程调试地址
 * 2. 监听二维码扫码状态接口 /login/qrcode/status
 * 3. 扫码成功后，等待页面跳转到 /explore
 * 4. 主动跳转到创作者发布页，确认创作者平台登录态
 * 5. 返回创作者相关 cookie
 */
export const rpaAction: SteelAction = async (task, _params) => {
    const reporter = task.reportService?.reportLoginStatus ?? (() => Promise.resolve());

    let page: Page;
    let isSelfClosing = false;

    // ========== 第一步：启动浏览器并打开登录页 ==========
    try {
        page = await task.createPage({
            url: "https://www.xiaohongshu.com/login",
        });
        if (!task._session?.id) throw new Error("Session ID 不存在");
        task._steelBrowser?.on("disconnected", () => {
            if (task.debug && isSelfClosing) {
                task.logger.info("执行结束，关闭浏览器！");
            } else {
                task.logger.info("浏览器被手动释放或意外关闭！");
            }
        });
    } catch (e) {
        task.logger.error("浏览器启动失败", e);
        return { code: 414, message: `浏览器启动失败: ${e}`, data: {} };
    }

    // ========== 第二步：生成远程调试地址 ==========
    const pageInfo = await task._client?.sessions.liveDetails(task._session.id);
    const pageId = pageInfo?.pages[0].id ?? "";

    const baseHost = task._session.websocketUrl?.replace(/^wss?:\/\//, "") ?? "";
    const connectAddress = new URLSearchParams({ baseHost, pageId }).toString();

    task.reportService?.settingSessionID?.(task._session?.id || "");

    await reporter(0, "浏览器启动成功，请使用小红书App扫码登录……", { connectAddress });

    // ========== 第三步：监听扫码状态与登录结果 ==========
    let userInfo: UserInfo = { uniqueId: "", avatar: "", name: "" };
    try {
        await new Promise<void>((resolve, reject) => {
            let finished = false;

            /** 清理所有监听器，防止重复触发 */
            const stopListening = () => {
                page.off("response", handleCheckQR);
            };

            const rejectOnce = (status: unknown) => {
                if (finished) return;
                finished = true;
                stopListening();
                reject(status);
            };

            const resolveOnce = () => {
                if (finished) return;
                finished = true;
                stopListening();
                resolve();
            };

            const continueAfterExplore = async () => {
                try {
                    if (!isExploreUrl(page.url())) {
                        await page.waitForURL((currentUrl) => isExploreUrl(currentUrl.toString()), {
                            timeout: 60000,
                        });
                    }

                    userInfo = mergeUserInfo(
                        userInfo,
                        await readUserInfo(page).catch(() => ({ name: "", avatar: "", uniqueId: "" })),
                    );
                    await reporter(0, "主站登录成功，正在跳转创作者发布页……", {
                        stage: "scanQrcode",
                        phase: "creatorPublishRedirect",
                        connectAddress,
                    });

                    const creatorUserInfoResponse = page.waitForResponse(
                        (response) =>
                            response.url().includes("/api/galaxy/user/info") &&
                            response.request().method() === "GET" &&
                            response.status() === 200,
                        { timeout: 20000 },
                    );

                    await page.goto(CREATOR_PUBLISH_URL, { waitUntil: "domcontentloaded", timeout: 20000 });

                    await page.waitForURL((currentUrl) => isCreatorPublishUrl(currentUrl.toString()), {
                        timeout: 20000,
                    });
                    await page.waitForSelector("#CreatorPlatform", { state: "visible", timeout: 20000 });

                    const creatorUserInfo = await creatorUserInfoResponse
                        .then(async (response) =>
                            extractCreatorUserInfo((await response.json()) as CreatorUserInfoPayload),
                        )
                        .catch(() => ({ name: "", avatar: "", uniqueId: "" }));
                    userInfo = mergeUserInfo(userInfo, creatorUserInfo);

                    await reporter(0, "扫码成功!", {
                        stage: "scanQrcode",
                        phase: "creatorPublishRedirect",
                        connectAddress,
                        userInfo,
                    });

                    if (!isCreatorPublishUrl(page.url())) {
                        throw createStageError("创作者发布页跳转失败", "creatorPublishRedirect");
                    }
                    resolveOnce();
                } catch (err) {
                    task.logger.error("登录结果处理异常", err);
                    rejectOnce(err);
                }
            };

            /**
             * 监听二维码扫码状态接口 /login/qrcode/status
             *
             * code_status 说明：
             *   1 = 小红书扫码成功
             *   2 = 微信扫码成功（同时返回登录信息）
             */
            const handleCheckQR = async (response: Response) => {
                try {
                    const url = response.url();
                    if (!url.includes("/login/qrcode/status") || response.request().method() !== "GET") return;

                    let jsonResponse: { code: number; data: { code_status: number; login_info: { user_id: string } } };
                    try {
                        const text = await response.text();
                        if (!text) {
                            task.logger.warn("扫码状态接口返回空响应", { url });
                            return;
                        }
                        jsonResponse = JSON.parse(text);
                    } catch (e) {
                        task.logger.warn("扫码状态接口响应解析失败", { url, error: e });
                        return;
                    }

                    const codeStatus = jsonResponse.data?.code_status;

                    if (jsonResponse.code === 0 && (codeStatus === 1 || codeStatus === 2)) {
                        // 微信扫码时直接从响应中获取 userId
                        if (codeStatus === 2) userInfo.uniqueId = jsonResponse.data.login_info.user_id;
                        task.debug && task.logger.info("扫码成功，等待页面跳转到 explore...");

                        page.off("response", handleCheckQR);
                        void continueAfterExplore();
                    }
                } catch (err) {
                    task.logger.error("用户信息获取错误", err);
                    rejectOnce(err);
                }
            };

            // 提前注册监听，防止丢失请求
            page.on("response", handleCheckQR);
        });
    } catch (e) {
        // 扫码状态码 → 用户友好提示的映射
        const errMap: Record<string, string> = {
            "0": "用户扫码已超时，请重试！",
            "1": "用户扫码后未点击确认，请重试！",
            "3": "二维码已过期，请重试！",
        };
        isSelfClosing = true;

        const errorMessage =
            typeof e === "string" && errMap[e] ? errMap[e] : e instanceof Error ? e.message : String(e);
        const stage =
            typeof e === "object" && e && "stage" in e
                ? String((e as { stage?: unknown }).stage ?? "scanQrcode")
                : "scanQrcode";

        return { code: 500, message: errorMessage, data: { stage, connectAddress } };
    }

    // ========== 第四步：收集 cookie 并返回结果 ==========
    const cookies = filterCreatorCookies((await task._steelBrowserContext?.cookies()) ?? []);
    const cookie = JSON.stringify(cookies);
    isSelfClosing = true;
    await page.close();

    return success({ connectAddress, cookie, userInfo }, "登录成功！");
};
